import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  MapPin, 
  ExternalLink, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  ArrowLeft, 
  Landmark, 
  Globe, 
  Building2, 
  Edit3, 
  Save, 
  Check, 
  FileText, 
  Layers, 
  Compass,
  Map as MapIcon,
  ListFilter,
  Users,
  Scale,
  ShieldCheck,
  Award,
  Zap,
  Mail,
  Phone,
  Copy,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  Clock,
  BookOpen,
  Info,
  HelpCircle,
  Briefcase,
  SlidersHorizontal,
  FileCheck,
  FileCode,
  Sparkles,
  RefreshCw,
  Terminal,
  Database,
  ChevronDown,
  X,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Gauge,
  BadgeCheck,
  ArrowUpRight
} from 'lucide-react';
import { ALL_NASPO_STATES, getNaspoStateByCode, NaspoRegionFilter, NASPO_REGIONS } from '../data/naspoStateData';
import { NaspoStateProfile, NaspoTeamMember, NaspoRospRule, NaspoDoingBusinessItem } from '../types/naspo';
import usGeoPaths from '../data/usStateGeoPaths.json';
import { db, doc, getDoc, setDoc, updateDoc, onSnapshot, collection, serverTimestamp, OperationType, handleFirestoreError } from '../firebase';
import { useAuth, VendorProfile } from '../AuthContext';
import { VendorProfileSwitcher } from './VendorProfileSwitcher';

export interface StateMapPathData {
  id: string;
  name: string;
  d: string;
  cx?: number;
  cy?: number;
}

// Full 50-State + DC Albers USA SVG Geometry from topojson
export const US_MAP_STATE_PATHS: StateMapPathData[] = usGeoPaths as StateMapPathData[];

export interface StateReadinessItem {
  id: string;
  category: 'sos' | 'identifiers' | 'portal' | 'codes' | 'programs' | 'thresholds';
  title: string;
  description: string;
  status: 'passed' | 'warning' | 'failed';
  score: number;
  maxScore: number;
  badge: string;
  details: string;
  remediation?: string;
  actionUrl?: string;
  actionLabel?: string;
}

export interface StateReadinessReport {
  overallScore: number;
  tier: 'Procurement Ready' | 'Substantially Ready' | 'Action Items Required';
  tierColor: 'emerald' | 'amber' | 'rose';
  isDomestic: boolean;
  sosStatus: string;
  items: StateReadinessItem[];
  passedCount: number;
  totalCount: number;
}

// State Procurement Readiness Evaluation Engine
export const computeStateReadiness = (
  state: NaspoStateProfile, 
  vendor: VendorProfile | null, 
  regData: { isRegistered?: boolean; vendorId?: string; username?: string; password?: string }
): StateReadinessReport => {
  const items: StateReadinessItem[] = [];
  
  // 1. Secretary of State (SOS) Registration & Jurisdiction Status
  const vendorState = vendor?.businessRegisteredState || vendor?.state || '';
  const isDomestic = Boolean(
    vendorState && 
    (vendorState.toLowerCase() === state.state.toLowerCase() || vendorState.toUpperCase() === state.stateCode.toUpperCase())
  );
  
  if (isDomestic) {
    items.push({
      id: 'sos',
      category: 'sos',
      title: 'Secretary of State (SOS) Qualification',
      description: `Domestic entity chartered in ${state.state}. Direct statutory standing for all state agency solicitations.`,
      status: 'passed',
      score: 20,
      maxScore: 20,
      badge: 'Domestic Qualified (100%)',
      details: `Entity is formed directly in ${state.state}. No out-of-state foreign authorization or reciprocal tax filings required.`
    });
  } else if (vendorState) {
    if (regData?.isRegistered) {
      items.push({
        id: 'sos',
        category: 'sos',
        title: 'Secretary of State (SOS) Qualification',
        description: `Foreign entity (chartered in ${vendorState}) with active state procurement standing in ${state.state}.`,
        status: 'passed',
        score: 18,
        maxScore: 20,
        badge: 'Foreign Standing Active',
        details: `Entity is chartered in ${vendorState} and recorded in ${state.state}'s procurement conduit.`
      });
    } else {
      items.push({
        id: 'sos',
        category: 'sos',
        title: 'Secretary of State (SOS) Qualification',
        description: `Foreign entity (chartered in ${vendorState}). Requires Certificate of Authority from ${state.state} SOS prior to contract award.`,
        status: 'warning',
        score: 12,
        maxScore: 20,
        badge: 'Foreign Filing Required',
        details: `Under ${state.state} corporate code, out-of-state contractors must hold a certificate of authority / good standing to execute public contracts.`,
        remediation: `File Foreign Entity Qualification with ${state.state} Secretary of State.`,
        actionUrl: state.vendorUrl || state.stateprocpubsolurl,
        actionLabel: `Open ${state.state} SOS Portal`
      });
    }
  } else {
    items.push({
      id: 'sos',
      category: 'sos',
      title: 'Secretary of State (SOS) Qualification',
      description: 'State of incorporation not specified in the active business entity profile.',
      status: 'failed',
      score: 5,
      maxScore: 20,
      badge: 'State Missing',
      details: 'Set your primary business registration state in your Entity Profile.',
      remediation: 'Update business incorporation state in Business Profile settings.'
    });
  }

  // 2. Taxpayer & Corporate Identifiers (EIN, DUNS/UEI, CAGE, State DOS)
  const hasEin = Boolean(vendor?.ein || vendor?.companyId);
  const hasUeiOrCage = Boolean(vendor?.samUei || vendor?.cageCode || vendor?.dunsNumber || vendor?.duns);
  const hasDosId = Boolean(vendor?.dosId || vendor?.companyId);

  if (hasEin && (hasUeiOrCage || hasDosId)) {
    items.push({
      id: 'identifiers',
      category: 'identifiers',
      title: 'Federal & Corporate Identifiers',
      description: `Federal EIN (${vendor?.ein || vendor?.companyId || 'Recorded'}) and SAM/CAGE credentials verified.`,
      status: 'passed',
      score: 20,
      maxScore: 20,
      badge: 'Fully Identified',
      details: `Tax ID: ${vendor?.ein || vendor?.companyId || 'On File'} • UEI/DUNS: ${vendor?.samUei || vendor?.dunsNumber || vendor?.cageCode || 'Verified'}`
    });
  } else if (hasEin) {
    items.push({
      id: 'identifiers',
      category: 'identifiers',
      title: 'Federal & Corporate Identifiers',
      description: `Federal Tax ID on file (${vendor?.ein || vendor?.companyId}). SAM UEI or CAGE code recommended for cooperative contracts.`,
      status: 'warning',
      score: 14,
      maxScore: 20,
      badge: 'EIN Verified',
      details: 'Tax ID is present, but federal SAM.gov UEI / CAGE code is not yet linked.',
      remediation: 'Add SAM.gov UEI or CAGE Code to your Entity Profile for cooperative contract eligibility.'
    });
  } else {
    items.push({
      id: 'identifiers',
      category: 'identifiers',
      title: 'Federal & Corporate Identifiers',
      description: 'Federal Tax Identification Number (EIN) is missing from active business entity.',
      status: 'failed',
      score: 0,
      maxScore: 20,
      badge: 'EIN Missing',
      details: 'State procurement departments require an official EIN for IRS W-9 verification.',
      remediation: 'Add your company EIN in the Entity Profile manager.'
    });
  }

  // 3. Central State Vendor Portal & Supplier Registry Status
  if (regData?.isRegistered && regData?.vendorId) {
    items.push({
      id: 'portal',
      category: 'portal',
      title: `${state.state} Supplier Registry Status`,
      description: `Actively registered in ${state.agencyName || state.state + ' Purchasing'} portal. Assigned Vendor ID: ${regData.vendorId}.`,
      status: 'passed',
      score: 25,
      maxScore: 25,
      badge: `Registered (ID: ${regData.vendorId})`,
      details: `Credentials and vendor identifier synced to vault. You are eligible for automated RFX notices and electronic bid submission.`
    });
  } else if (regData?.isRegistered) {
    items.push({
      id: 'portal',
      category: 'portal',
      title: `${state.state} Supplier Registry Status`,
      description: `Marked as registered in ${state.state} vendor vault. Official State Vendor ID not yet recorded.`,
      status: 'warning',
      score: 18,
      maxScore: 25,
      badge: 'Registered (No ID)',
      details: 'You are marked as registered, but entering the state supplier ID enables automated bid matching.',
      remediation: `Add your state vendor ID in the Vendor Credentials Vault tab.`
    });
  } else {
    items.push({
      id: 'portal',
      category: 'portal',
      title: `${state.state} Supplier Registry Status`,
      description: `Not yet registered in ${state.state}'s central e-procurement system.`,
      status: 'failed',
      score: 0,
      maxScore: 25,
      badge: 'Action Required',
      details: `Registration is required to receive electronic RFx bid solicitations and submit formal proposals to ${state.state}.`,
      remediation: `Complete online registration in the ${state.state} vendor portal.`,
      actionUrl: state.vendorUrl,
      actionLabel: `Register on ${state.state} Portal`
    });
  }

  // 4. Industry Classification & Commodity Codes (NAICS & NIGP)
  const naicsCount = vendor?.naicsCodes?.length || 0;
  const nigpCount = vendor?.nigpCodes?.length || 0;

  if (naicsCount > 0 || nigpCount > 0) {
    items.push({
      id: 'codes',
      category: 'codes',
      title: 'Industry & Commodity Codes (NAICS / NIGP)',
      description: `${naicsCount} NAICS codes & ${nigpCount} NIGP commodity codes mapped for automated RFX matching.`,
      status: 'passed',
      score: 15,
      maxScore: 15,
      badge: `${naicsCount + nigpCount} Codes Active`,
      details: `Tracked NAICS: ${vendor?.naicsCodes?.slice(0, 4).join(', ') || 'None'} ${naicsCount > 4 ? `(+${naicsCount - 4} more)` : ''}`
    });
  } else {
    items.push({
      id: 'codes',
      category: 'codes',
      title: 'Industry & Commodity Codes (NAICS / NIGP)',
      description: 'No NAICS or NIGP commodity codes associated with this entity.',
      status: 'warning',
      score: 5,
      maxScore: 15,
      badge: 'Codes Missing',
      details: 'State procurement divisions use NAICS and NIGP codes to route solicitations to matching suppliers.',
      remediation: 'Add NAICS / NIGP industry codes in your Entity Profile.'
    });
  }

  // 5. Targeted Business Preference & Socio-Economic Programs
  const designations = vendor?.designations || vendor?.capabilities || [];
  const statePrograms = state.preferencePrograms || [];
  const matchingPrefs = statePrograms.filter((p: string) => 
    designations.some((d: string) => p.toLowerCase().includes(d.toLowerCase()) || d.toLowerCase().includes(p.toLowerCase()) || (d.includes('SB') && p.includes('Small')))
  );

  if (matchingPrefs.length > 0) {
    items.push({
      id: 'programs',
      category: 'programs',
      title: 'Targeted Business Preferences & Set-Asides',
      description: `Entity qualifies for ${matchingPrefs.length} state preference program(s): ${matchingPrefs.join(', ')}.`,
      status: 'passed',
      score: 10,
      maxScore: 10,
      badge: 'Preference Eligible',
      details: `Your entity certifications (${designations.slice(0, 2).join(', ')}) provide competitive preference points on ${state.state} solicitations.`
    });
  } else if (statePrograms.length > 0) {
    items.push({
      id: 'programs',
      category: 'programs',
      title: 'Targeted Business Preferences & Set-Asides',
      description: `${state.state} offers preference programs: ${statePrograms.slice(0, 2).join(', ')}. Open commercial bidding track available.`,
      status: 'passed',
      score: 8,
      maxScore: 10,
      badge: 'Commercial Track',
      details: 'Eligible for open public solicitations. Add state-certified MBE/WBE/SBE/SDVOSB designations if applicable.'
    });
  } else {
    items.push({
      id: 'programs',
      category: 'programs',
      title: 'Targeted Business Preferences & Set-Asides',
      description: `Open competitive bidding without state preference set-asides.`,
      status: 'passed',
      score: 10,
      maxScore: 10,
      badge: 'Open Competitive',
      details: 'All qualified vendors evaluated equally under standard NASPO procurement code guidelines.'
    });
  }

  // 6. Statutory Bidding Thresholds & Contract Capacity
  items.push({
    id: 'thresholds',
    category: 'thresholds',
    title: 'Statutory Purchasing Limits & Thresholds',
    description: `Informal quotes up to ${state.thresholds.informalThreshold}. Formal sealed bids required above ${state.thresholds.formalThreshold}.`,
    status: 'passed',
    score: 10,
    maxScore: 10,
    badge: 'Thresholds Mapped',
    details: `Entity can bid directly on informal agency purchases (<${state.thresholds.informalThreshold}) and statewide master contracts (>${state.thresholds.formalThreshold}).`
  });

  const totalScore = items.reduce((acc, curr) => acc + curr.score, 0);
  const passedCount = items.filter(i => i.status === 'passed').length;
  
  let tier: 'Procurement Ready' | 'Substantially Ready' | 'Action Items Required' = 'Procurement Ready';
  let tierColor: 'emerald' | 'amber' | 'rose' = 'emerald';

  if (totalScore >= 85) {
    tier = 'Procurement Ready';
    tierColor = 'emerald';
  } else if (totalScore >= 60) {
    tier = 'Substantially Ready';
    tierColor = 'amber';
  } else {
    tier = 'Action Items Required';
    tierColor = 'rose';
  }

  return {
    overallScore: totalScore,
    tier,
    tierColor,
    isDomestic,
    sosStatus: isDomestic ? 'Domestic Qualified' : (regData?.isRegistered ? 'Foreign Standing Active' : 'Foreign Qualification Required'),
    items,
    passedCount,
    totalCount: items.length
  };
};

export const StateVendorRegistriesView: React.FC = () => {
  const { vendorProfile, activeProfileId, updateCurrentProfile, updateStateCredential } = useAuth();
  const [firestoreProfiles, setFirestoreProfiles] = useState<Record<string, any>>({});
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<NaspoRegionFilter>('All');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [hoveredState, setHoveredState] = useState<NaspoStateProfile | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [mapDisplayMode, setMapDisplayMode] = useState<'svg' | 'grid'>('svg');
  
  // Tabs matching NASPO.org/states/<statename> exactly + Integrated enhancements
  const [activeDossierTab, setActiveDossierTab] = useState<'overview' | 'readiness' | 'team' | 'rosp' | 'doingBusiness' | 'credentials' | 'automation'>('overview');
  
  // ROSP filter & search
  const [rospSearch, setRospSearch] = useState<string>('');
  const [rospCategoryFilter, setRospCategoryFilter] = useState<string>('All');

  // Regional states search
  const [regionalSearch, setRegionalSearch] = useState<string>('');

  // Staff search
  const [staffSearch, setStaffSearch] = useState<string>('');

  // Editing credentials form state
  const [isEditingCredentials, setIsEditingCredentials] = useState<boolean>(false);
  const [formVendorId, setFormVendorId] = useState<string>('');
  const [formUsername, setFormUsername] = useState<string>('');
  const [formPassword, setFormPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formIsRegistered, setFormIsRegistered] = useState<boolean>(false);
  const [savingCredentials, setSavingCredentials] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Bulk Automation Engine state
  const [filterMode, setFilterMode] = useState<'all' | 'hot' | 'custom'>('hot');
  const [selectedBulkStates, setSelectedBulkStates] = useState<string[]>(['TX', 'CA', 'NY', 'FL', 'IL', 'PA', 'OH']);
  const [runningPipeline, setRunningPipeline] = useState<boolean>(false);
  const [pipelineLogs, setPipelineLogs] = useState<string[]>([]);
  const [currentPipelineIndex, setCurrentPipelineIndex] = useState<number>(-1);
  const [pipelineSpeed, setPipelineSpeed] = useState<'standard' | 'turbo'>('turbo');
  const terminalEndRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 1. Subscribe to Firestore `state_profiles` collection to retrieve any saved credentials / notes
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'state_profiles'), (snapshot) => {
      const records: Record<string, any> = {};
      snapshot.docs.forEach((docSnap) => {
        records[docSnap.id.toUpperCase()] = docSnap.data();
      });
      setFirestoreProfiles(records);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'state_profiles');
    });
    return () => unsubscribe();
  }, []);

  // 2. Auto-scroll terminal on new log lines
  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollTop = terminalEndRef.current.scrollHeight;
    }
  }, [pipelineLogs]);

  // Combined State Profile data (NASPO Base + Scoped Entity Credentials & Registration)
  const combinedStates: (NaspoStateProfile & { readinessReport?: StateReadinessReport })[] = useMemo(() => {
    const currentEntityId = activeProfileId || vendorProfile?.id;
    const entityRegistrations = vendorProfile?.stateRegistrations || {};

    return ALL_NASPO_STATES.map((base) => {
      const codeUpper = base.stateCode.toUpperCase();
      const fsData = firestoreProfiles[codeUpper];
      const entityFsData = (fsData?.entities && currentEntityId) ? fsData.entities[currentEntityId] : null;
      const profileRegData = entityRegistrations[codeUpper];

      // Entity-specific registration precedence: AuthContext active profile -> Firestore entity sub-map -> legacy top-level
      const effectiveData = profileRegData || entityFsData || fsData;

      const isRegistered = effectiveData?.isRegistered !== undefined 
        ? effectiveData.isRegistered 
        : base.isRegistered;
      
      const vendorId = effectiveData?.vendorId || base.vendorId || '';
      const username = effectiveData?.username || base.username || '';
      const password = effectiveData?.password || base.password || '';

      const stateProfileWithReg = {
        ...base,
        isRegistered,
        vendorId,
        username,
        password,
        lastUpdated: effectiveData?.lastUpdated || effectiveData?.updatedAt
      };

      const readinessReport = computeStateReadiness(stateProfileWithReg, vendorProfile, {
        isRegistered,
        vendorId,
        username,
        password
      });

      return {
        ...stateProfileWithReg,
        readinessReport
      };
    });
  }, [firestoreProfiles, vendorProfile, activeProfileId]);

  // Total registered states for active Business Entity
  const activeEntityRegisteredCount = useMemo(() => {
    return combinedStates.filter(s => s.isRegistered).length;
  }, [combinedStates]);

  // Currently selected state
  const activeState = useMemo(() => {
    if (!selectedStateCode) return null;
    return combinedStates.find(s => s.stateCode.toUpperCase() === selectedStateCode.toUpperCase()) || null;
  }, [combinedStates, selectedStateCode]);

  // Active state readiness report
  const activeReadiness = useMemo(() => {
    if (!activeState) return null;
    return computeStateReadiness(activeState, vendorProfile, {
      isRegistered: activeState.isRegistered,
      vendorId: activeState.vendorId,
      username: activeState.username,
      password: activeState.password
    });
  }, [activeState, vendorProfile]);

  // Populate credential edit form when activeState OR active Business Entity changes
  useEffect(() => {
    if (activeState) {
      setFormVendorId(activeState.vendorId || '');
      setFormUsername(activeState.username || '');
      setFormPassword(activeState.password || '');
      setFormIsRegistered(activeState.isRegistered || false);
      setIsEditingCredentials(false);
      setShowPassword(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeState?.stateCode, activeProfileId, vendorProfile?.id]);

  // Save Credentials to Firestore and synchronize with active Vendor Profile
  const handleSaveCredentials = async () => {
    if (!activeState) return;
    setSavingCredentials(true);
    const entityCompanyName = vendorProfile?.companyName || 'Active Entity';

    try {
      await updateStateCredential(activeState.stateCode.toUpperCase(), {
        stateCode: activeState.stateCode.toUpperCase(),
        stateName: activeState.state,
        vendorId: formVendorId.trim(),
        username: formUsername.trim(),
        password: formPassword.trim(),
        isRegistered: formIsRegistered,
        registrationStatus: formIsRegistered ? 'active' : 'not_registered',
        customLoginUrl: activeState.vendorUrl || activeState.loginUrl || '',
        portalUrl: activeState.vendorUrl || activeState.loginUrl || '',
      });

      setIsEditingCredentials(false);
      setToastMessage(`Credentials synced for ${entityCompanyName} in ${activeState.state}!`);
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err: any) {
      handleFirestoreError(err, OperationType.WRITE, `state_profiles/${activeState.stateCode}`);
      setToastMessage(`Error saving credentials: ${err.message}`);
      setTimeout(() => setToastMessage(null), 4000);
    } finally {
      setSavingCredentials(false);
    }
  };

  // Quick Copy Helper
  const handleCopy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  // Filtered States list for search & combobox
  const filteredStates = useMemo(() => {
    return combinedStates.filter((s) => {
      const matchesSearch = searchTerm.trim() === '' || 
        s.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.stateCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.capital.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.cpo?.name && s.cpo.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (s.agencyName && s.agencyName.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesRegion = selectedRegion === 'All' || s.region === selectedRegion;

      return matchesSearch && matchesRegion;
    });
  }, [combinedStates, searchTerm, selectedRegion]);

  // Dedicated Regional States List for Sidebar
  const regionalStatesList = useMemo(() => {
    return combinedStates.filter((s) => {
      const matchesRegion = selectedRegion === 'All' || s.region === selectedRegion;
      const matchesSearch = regionalSearch.trim() === '' || 
        s.state.toLowerCase().includes(regionalSearch.toLowerCase()) ||
        s.stateCode.toLowerCase().includes(regionalSearch.toLowerCase()) ||
        s.capital.toLowerCase().includes(regionalSearch.toLowerCase()) ||
        (s.cpo?.name && s.cpo.name.toLowerCase().includes(regionalSearch.toLowerCase())) ||
        (s.agencyName && s.agencyName.toLowerCase().includes(regionalSearch.toLowerCase()));

      return matchesRegion && matchesSearch;
    });
  }, [combinedStates, selectedRegion, regionalSearch]);

  // Unique ROSP categories for active state
  const rospCategories = useMemo(() => {
    if (!activeState || !activeState.rosp) return ['All'];
    const cats = new Set<string>();
    activeState.rosp.forEach(r => {
      if (r.category) cats.add(r.category);
    });
    return ['All', ...Array.from(cats).sort()];
  }, [activeState]);

  // Filtered ROSP rules
  const filteredRospRules = useMemo(() => {
    if (!activeState || !activeState.rosp) return [];
    return activeState.rosp.filter(r => {
      const matchCat = rospCategoryFilter === 'All' || r.category === rospCategoryFilter;
      const textToSearch = [
        r.category,
        r.citation,
        r.citationLanguage,
        r.statutoryLanguage,
        r.status,
        r.definition,
        r.description
      ].filter(Boolean).join(' ').toLowerCase();

      const matchSearch = rospSearch.trim() === '' || textToSearch.includes(rospSearch.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [activeState, rospCategoryFilter, rospSearch]);

  // Filtered staff
  const filteredTeam = useMemo(() => {
    if (!activeState || !activeState.team) return [];
    if (!staffSearch.trim()) return activeState.team;
    const lower = staffSearch.toLowerCase();
    return activeState.team.filter(m => 
      m.name.toLowerCase().includes(lower) ||
      m.title.toLowerCase().includes(lower) ||
      m.email.toLowerCase().includes(lower) ||
      m.phone.toLowerCase().includes(lower)
    );
  }, [activeState, staffSearch]);

  // Previous & Next state navigation
  const navigateState = (direction: 'prev' | 'next') => {
    if (!activeState) return;
    const currentIndex = ALL_NASPO_STATES.findIndex(s => s.stateCode === activeState.stateCode);
    if (currentIndex === -1) return;
    let nextIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (nextIndex < 0) nextIndex = ALL_NASPO_STATES.length - 1;
    if (nextIndex >= ALL_NASPO_STATES.length) nextIndex = 0;
    setSelectedStateCode(ALL_NASPO_STATES[nextIndex].stateCode);
  };

  // Trigger Bulk Registrar Pipeline Simulation with accurate state endpoints
  const runBulkRegistration = async () => {
    if (selectedBulkStates.length === 0) return;
    const entityKey = activeProfileId || vendorProfile?.id || 'primary';
    const entityName = vendorProfile?.companyName || 'Authenticated Vendor';
    const entityEin = vendorProfile?.ein || 'On File';

    setRunningPipeline(true);
    setPipelineLogs([
      `[SYSTEM] Initializing 50-State Registrar Engine v5.2 (Multi-Entity Synced)...`,
      `[ENTITY] Active Business Entity: ${entityName} | EIN: ${entityEin}`,
      `[TARGET] Queued ${selectedBulkStates.length} state procurement portals for registration sync.`
    ]);

    const delay = pipelineSpeed === 'turbo' ? 400 : 1000;
    const updatedStateRegs: Record<string, any> = { ...(vendorProfile?.stateRegistrations || {}) };

    for (let i = 0; i < selectedBulkStates.length; i++) {
      setCurrentPipelineIndex(i);
      const code = selectedBulkStates[i];
      const stateObj = getNaspoStateByCode(code);
      const stateName = stateObj?.state || code;
      const portalUrl = stateObj?.vendorUrl || stateObj?.registrationUrl || `https://purchasing.${code.toLowerCase()}.gov/`;

      setPipelineLogs(prev => [
        ...prev,
        `[CONN] [${code}] Connecting to ${stateName} State Purchasing Division on behalf of ${entityName}...`,
        `[FORM] [${code}] Querying vendor onboarding API: ${portalUrl.slice(0, 48)}...`
      ]);

      await new Promise(res => setTimeout(res, delay));

      // Generated Mock State Vendor ID
      const generatedVendorId = `${code}-${Math.floor(100000 + Math.random() * 900000)}`;
      const generatedUsername = `vendor_${code.toLowerCase()}_${entityName.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toLowerCase()}`;

      try {
        const stateDocRef = doc(db, 'state_profiles', code.toUpperCase());
        await setDoc(stateDocRef, {
          state: stateName,
          stateCode: code,
          vendorId: generatedVendorId,
          username: generatedUsername,
          isRegistered: true,
          updatedAt: serverTimestamp(),
          registeredViaAutomation: true,
          [`entities.${entityKey}`]: {
            entityId: entityKey,
            entityName: entityName,
            vendorId: generatedVendorId,
            username: generatedUsername,
            isRegistered: true,
            updatedAt: serverTimestamp()
          }
        }, { merge: true });

        updatedStateRegs[code.toUpperCase()] = {
          stateCode: code.toUpperCase(),
          stateName: stateName,
          vendorId: generatedVendorId,
          username: generatedUsername,
          isRegistered: true,
          updatedAt: new Date().toISOString()
        };

        setPipelineLogs(prev => [
          ...prev,
          `[SUCCESS] [${code}] Registered successfully for ${entityName}! Assigned Vendor ID: ${generatedVendorId}`,
          `[SYNC] [${code}] Synced credentials to secure vault & Entity profile.`
        ]);
      } catch (err: any) {
        setPipelineLogs(prev => [
          ...prev,
          `[ERROR] [${code}] Failed writing to Firestore: ${err.message}`
        ]);
      }

      await new Promise(res => setTimeout(res, delay / 2));
    }

    try {
      await updateCurrentProfile({
        stateRegistrations: updatedStateRegs
      });
    } catch (err) {
      console.warn("Could not sync bulk registrations to active vendor profile:", err);
    }

    setPipelineLogs(prev => [
      ...prev,
      `[COMPLETE] All ${selectedBulkStates.length} state registrations processed and synced with ${entityName}! 🚀`
    ]);
    setRunningPipeline(false);
    setCurrentPipelineIndex(-1);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 font-sans pb-16">
      {/* TOAST NOTIFICATION */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#002B49] text-white px-5 py-3 rounded-lg shadow-xl border border-amber-400 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-3 duration-200">
          <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 1. NASPO OFFICIAL HEADER BANNER WITH ENTITY PROFILE SYNC BAR */}
      {/* ========================================================================= */}
      <header className="bg-[#002B49] text-white border-b border-[#001D33] shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-amber-400 shrink-0 shadow-inner">
              <Landmark className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold tracking-widest text-amber-400 uppercase">NASPO.org Official Clone</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-mono">50 States + DC</span>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
                State Profiles & Procurement Directory
              </h1>
            </div>
          </div>

          {/* Active Business Entity Profile Switcher & Actions */}
          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Entity Switcher Component */}
            <div className="flex items-center">
              <VendorProfileSwitcher variant="compact" />
            </div>

            {/* Quick Search & State Select Combobox */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-48 sm:w-56 px-3.5 py-1.5 bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl text-xs text-left flex items-center justify-between text-white transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer"
              >
                <span className="truncate">
                  {selectedStateCode ? (
                    <span className="font-medium text-amber-300">
                      {activeState?.state} ({activeState?.stateCode})
                    </span>
                  ) : (
                    <span className="text-slate-300">Select State...</span>
                  )}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-300 shrink-0 ml-1.5" />
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-1.5 w-64 bg-white text-slate-800 rounded-lg shadow-2xl border border-slate-200 py-1 z-50 max-h-80 overflow-y-auto">
                  <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search states..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#002B49]"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="py-1">
                    {filteredStates.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500 text-center">No states found</div>
                    ) : (
                      filteredStates.map((st) => (
                        <button
                          key={st.stateCode}
                          onClick={() => {
                            setSelectedStateCode(st.stateCode);
                            setIsDropdownOpen(false);
                            setSearchTerm('');
                          }}
                          className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-slate-100 transition-colors ${
                            selectedStateCode === st.stateCode ? 'bg-amber-50 font-bold text-[#002B49]' : ''
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-5 text-center font-mono font-bold text-slate-500">{st.stateCode}</span>
                            <span>{st.state}</span>
                          </span>
                          {st.isRegistered && (
                            <span className="w-2 h-2 rounded-full bg-emerald-500" title="Registered for active entity" />
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-black/20 p-0.5 rounded-xl border border-white/10">
              <button
                onClick={() => setMapDisplayMode('svg')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all cursor-pointer ${
                  mapDisplayMode === 'svg'
                    ? 'bg-amber-400 text-slate-900 shadow-sm font-bold'
                    : 'text-slate-200 hover:text-white'
                }`}
                title="Interactive SVG Map"
              >
                <MapIcon className="w-3.5 h-3.5" />
                <span>Map</span>
              </button>
              <button
                onClick={() => setMapDisplayMode('grid')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all cursor-pointer ${
                  mapDisplayMode === 'grid'
                    ? 'bg-amber-400 text-slate-900 shadow-sm font-bold'
                    : 'text-slate-200 hover:text-white'
                }`}
                title="50-State Grid"
              >
                <ListFilter className="w-3.5 h-3.5" />
                <span>Directory</span>
              </button>
            </div>
          </div>
        </div>

        {/* Dynamic Business Entity Sync Status Bar */}
        <div className="bg-[#002038] border-t border-white/10 px-4 sm:px-6 lg:px-8 py-2">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5">
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-slate-300">
                Synced Business Entity:
              </span>
              <span className="font-bold text-amber-300 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-amber-400" />
                {vendorProfile?.companyName || 'QUANTUM AI INC'}
                {vendorProfile?.label && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-normal">
                    {vendorProfile.label}
                  </span>
                )}
              </span>
              {vendorProfile?.ein && (
                <span className="text-[11px] font-mono text-slate-400 hidden sm:inline">
                  EIN: {vendorProfile.ein}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 text-[11px] text-slate-300">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>
                  <strong className="text-white">{activeEntityRegisteredCount}</strong> of 51 States Registered
                </span>
              </span>
              <span className="text-slate-500">•</span>
              <span>
                Charter State: <strong className="text-amber-200">{vendorProfile?.businessRegisteredState || 'South Dakota'}</strong>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ========================================================================= */}
      {/* 2. MAIN CONTAINER */}
      {/* ========================================================================= */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">

        {/* BREADCRUMB / ACTIVE STATE BANNER */}
        {selectedStateCode && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedStateCode(null)}
                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to States Map</span>
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-xs font-bold text-[#002B49] flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                {activeState?.state} State Procurement Profile
              </span>
            </div>

            {/* Pagination Controls between states */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateState('prev')}
                className="px-2.5 py-1 text-xs border border-slate-200 rounded-md hover:bg-slate-50 flex items-center gap-1 text-slate-600 cursor-pointer"
                title="Previous State"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Previous</span>
              </button>
              <button
                onClick={() => navigateState('next')}
                className="px-2.5 py-1 text-xs border border-slate-200 rounded-md hover:bg-slate-50 flex items-center gap-1 text-slate-600 cursor-pointer"
                title="Next State"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 1: MAP / DIRECTORY (When no state is selected) */}
        {/* ========================================================================= */}
        {!selectedStateCode && (
          <div className="space-y-6">
            {/* Description Subheader */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-bold text-[#002B49] tracking-tight">
                      Explore State Procurement Profiles
                    </h2>
                    <span className="text-xs font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md">
                      Live Entity Sync
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Click any state to inspect official leadership contacts, statutory Rules of State Procurement (ROSP), Doing Business guidelines, and vendor readiness assessments calculated specifically for <strong className="text-slate-800">{vendorProfile?.companyName || 'your active entity'}</strong>.
                  </p>
                </div>

                {/* Region Filter Buttons */}
                <div className="flex flex-wrap items-center gap-1.5 bg-slate-100 p-1.5 rounded-lg border border-slate-200 shrink-0">
                  {NASPO_REGIONS.map((region) => (
                    <button
                      key={region}
                      onClick={() => setSelectedRegion(region)}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition-all cursor-pointer ${
                        selectedRegion === region
                          ? 'bg-[#002B49] text-white shadow-sm font-semibold'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                      }`}
                    >
                      {region}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* INTERACTIVE US MAP & REGIONAL STATES DIRECTORY */}
            {mapDisplayMode === 'svg' ? (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* 1. UNOBSTRUCTED US MAP CANVAS */}
                <div className="lg:col-span-8 xl:col-span-8 2xl:col-span-9 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  {/* Map Subtitle & Legend */}
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
                    <div className="flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <span className="w-3.5 h-3.5 rounded-sm bg-[#004F71] inline-block border border-white" />
                        NASPO State
                      </span>
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <span className="w-3.5 h-3.5 rounded-sm bg-[#EAA023] inline-block border border-white" />
                        Hover / Active
                      </span>
                      <span className="flex items-center gap-1.5 font-medium text-slate-700">
                        <span className="w-3.5 h-3.5 rounded-sm bg-emerald-600 inline-block border border-white" />
                        Registered ({vendorProfile?.companyName?.slice(0, 14) || 'Active Entity'})
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      Hover state to inspect readiness • Click to open full dossier
                    </div>
                  </div>

                  {/* SVG Vector Map Container - 100% Unobstructed */}
                  <div className="relative w-full aspect-[16/10] select-none flex items-center justify-center">
                    <svg
                      viewBox="0 0 960 600"
                      className="w-full h-full drop-shadow-sm"
                      style={{ maxHeight: '620px' }}
                    >
                      <g className="states-layer">
                        {US_MAP_STATE_PATHS.map((statePath) => {
                          const stateData = combinedStates.find(s => s.stateCode === statePath.id);
                          const isHovered = hoveredState?.stateCode === statePath.id;
                          const isRegistered = stateData?.isRegistered;
                          const isRegionMatch = selectedRegion === 'All' || stateData?.region === selectedRegion;

                          let fillColor = '#004F71'; // Official NASPO Deep Blue/Teal
                          if (!isRegionMatch) fillColor = '#CBD5E1'; // Dimmed if filtered out
                          if (isRegistered) fillColor = '#059669'; // Emerald if registered in vault
                          if (isHovered) fillColor = '#EAA023'; // Warm Amber/Gold on hover

                          return (
                            <path
                              key={statePath.id}
                              d={statePath.d}
                              fill={fillColor}
                              stroke="#FFFFFF"
                              strokeWidth={isHovered ? 2.5 : 1}
                              strokeLinejoin="round"
                              className="cursor-pointer transition-colors duration-150"
                              onClick={() => {
                                setSelectedStateCode(statePath.id);
                              }}
                              onMouseEnter={(e) => {
                                if (stateData) {
                                  setHoveredState(stateData);
                                  setTooltipPos({ x: e.clientX, y: e.clientY });
                                }
                              }}
                              onMouseMove={(e) => {
                                setTooltipPos({ x: e.clientX, y: e.clientY });
                              }}
                              onMouseLeave={() => {
                                setHoveredState(null);
                              }}
                            />
                          );
                        })}
                      </g>

                      {/* State Centroid Label Abbreviations */}
                      <g className="labels-layer pointer-events-none">
                        {US_MAP_STATE_PATHS.map((statePath) => {
                          if (!statePath.cx || !statePath.cy) return null;
                          if (['VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC'].includes(statePath.id)) return null;

                          return (
                            <text
                              key={`label-${statePath.id}`}
                              x={statePath.cx}
                              y={statePath.cy}
                              textAnchor="middle"
                              alignmentBaseline="central"
                              fontSize="10"
                              fontWeight="bold"
                              fill="#FFFFFF"
                              className="drop-shadow"
                            >
                              {statePath.id}
                            </text>
                          );
                        })}
                      </g>
                    </svg>

                    {/* HOVER TOOLTIP WITH READINESS PREVIEW */}
                    {hoveredState && (
                      <div
                        className="fixed z-50 pointer-events-none bg-[#002B49] text-white px-3.5 py-2.5 rounded-lg shadow-xl border border-amber-400 text-xs w-68 animate-in fade-in zoom-in-95 duration-100"
                        style={{
                          left: `${tooltipPos.x + 16}px`,
                          top: `${tooltipPos.y + 16}px`
                        }}
                      >
                        <div className="flex items-center justify-between border-b border-white/10 pb-1.5 mb-1.5">
                          <div className="font-bold text-sm text-amber-400 flex items-center gap-1.5">
                            <span>{hoveredState.state}</span>
                            <span className="font-mono text-xs text-white/80">({hoveredState.stateCode})</span>
                          </div>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300 font-medium">
                            {hoveredState.region}
                          </span>
                        </div>
                        <div className="space-y-1.5 text-slate-200 text-[11px]">
                          <div><span className="text-slate-400">Capital:</span> {hoveredState.capital}</div>
                          {hoveredState.cpo && (
                            <div>
                              <span className="text-slate-400">CPO:</span> <span className="font-medium text-white">{hoveredState.cpo.name}</span>
                            </div>
                          )}
                          
                          {/* Readiness & Registration Status */}
                          <div className="pt-1 border-t border-white/10 flex items-center justify-between">
                            <span className="text-slate-400">Status for {vendorProfile?.companyName?.slice(0, 10) || 'Entity'}:</span>
                            {hoveredState.isRegistered ? (
                              <span className="text-emerald-400 font-bold flex items-center gap-1">
                                <Check className="w-3 h-3" /> Registered
                              </span>
                            ) : (
                              <span className="text-amber-300 font-medium">
                                Action Needed
                              </span>
                            )}
                          </div>

                          <div className="pt-1 text-[10px] text-amber-300 font-semibold flex items-center gap-1">
                            <span>Click state to view full profile</span>
                            <span>→</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. DEDICATED REGIONAL STATES SIDEBAR (UNOBSTRUCTED OUTSIDE OF MAP) */}
                <div className="lg:col-span-4 xl:col-span-4 2xl:col-span-3 bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex flex-col h-[650px]">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                    <div>
                      <h3 className="font-bold text-sm text-[#002B49] flex items-center gap-2">
                        <Globe className="w-4 h-4 text-[#004F71]" />
                        {selectedRegion === 'All' ? 'All 50 States + DC' : `${selectedRegion} States`}
                      </h3>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {selectedRegion === 'All' ? 'Nationwide state directory' : `States in the ${selectedRegion} region`}
                      </p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-[#004F71]/10 text-[#004F71]">
                      {regionalStatesList.length} {regionalStatesList.length === 1 ? 'State' : 'States'}
                    </span>
                  </div>

                  {/* Intra-Region Search */}
                  <div className="relative mb-3">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      placeholder={`Search ${selectedRegion === 'All' ? 'all states' : selectedRegion + ' states'}...`}
                      value={regionalSearch}
                      onChange={(e) => setRegionalSearch(e.target.value)}
                      className="w-full pl-8 pr-7 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#002B49]"
                    />
                    {regionalSearch && (
                      <button
                        onClick={() => setRegionalSearch('')}
                        className="absolute right-2 top-2 text-slate-400 hover:text-slate-600 text-xs w-4 h-4 flex items-center justify-center cursor-pointer"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* Scrollable List of Regional States with Readiness Scores */}
                  <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
                    {regionalStatesList.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500 bg-slate-50 rounded-lg border border-slate-100">
                        No states found matching "{regionalSearch}" in the {selectedRegion} region.
                      </div>
                    ) : (
                      regionalStatesList.map((st) => {
                        const isHovered = hoveredState?.stateCode === st.stateCode;
                        const score = st.readinessReport?.overallScore || 0;
                        return (
                          <button
                            key={st.stateCode}
                            onClick={() => setSelectedStateCode(st.stateCode)}
                            onMouseEnter={() => setHoveredState(st)}
                            onMouseLeave={() => setHoveredState(null)}
                            className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer group ${
                              isHovered
                                ? 'bg-amber-50 border-amber-300 shadow-sm translate-x-0.5'
                                : 'bg-white border-slate-100 hover:bg-slate-50 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div
                                className={`w-7 h-7 rounded-lg flex items-center justify-center font-mono font-bold text-xs shrink-0 transition-colors ${
                                  isHovered
                                    ? 'bg-[#EAA023] text-slate-900 shadow-sm'
                                    : st.isRegistered
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-[#004F71] text-white'
                                }`}
                              >
                                {st.stateCode}
                              </div>
                              <div className="min-w-0">
                                <div className="font-bold text-xs text-slate-900 group-hover:text-[#002B49] truncate flex items-center gap-1.5">
                                  <span>{st.state}</span>
                                  {st.isRegistered && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Registered in vault" />
                                  )}
                                </div>
                                <div className="text-[10px] text-slate-400 truncate">
                                  {st.capital} • {score}% Readiness
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5 shrink-0">
                              {st.isRegistered ? (
                                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-0.5">
                                  <Check className="w-2.5 h-2.5" /> Saved
                                </span>
                              ) : (
                                <span className="text-[10px] font-bold text-slate-400 group-hover:text-[#002B49]">
                                  {score}%
                                </span>
                              )}
                              <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all" />
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* GRID VIEW OF 50 STATES */
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredStates.map((st) => (
                  <div
                    key={st.stateCode}
                    onClick={() => setSelectedStateCode(st.stateCode)}
                    className="bg-white rounded-xl p-4 border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-[#002B49] text-white flex items-center justify-center font-bold text-xs group-hover:bg-[#004F71] transition-colors">
                            {st.stateCode}
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-900 text-sm group-hover:text-[#002B49] transition-colors">
                              {st.state}
                            </h3>
                            <div className="text-xs text-slate-500">{st.capital} • {st.region}</div>
                          </div>
                        </div>
                        {st.isRegistered && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                            <Check className="w-2.5 h-2.5" />
                            Saved
                          </span>
                        )}
                      </div>

                      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                        <span>Readiness for {vendorProfile?.label || 'Entity'}:</span>
                        <span className={`font-bold ${
                          (st.readinessReport?.overallScore || 0) >= 85 ? 'text-emerald-700' :
                          (st.readinessReport?.overallScore || 0) >= 60 ? 'text-amber-700' : 'text-rose-700'
                        }`}>
                          {st.readinessReport?.overallScore || 0}%
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs text-[#002B49] font-semibold group-hover:text-amber-600">
                      <span>View Profile & Rules</span>
                      <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 2: FULL STATE PROFILE WITH READINESS ASSESSMENT */}
        {/* ========================================================================= */}
        {activeState && (
          <div className="space-y-6">
            
            {/* 1. STATE HEADER & BUSINESS ENTITY SYNC CARD */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                
                {/* State Title & Flag */}
                <div className="flex items-start gap-4">
                  {activeState.flagUrl && (
                    <img
                      src={activeState.flagUrl}
                      alt={`${activeState.state} State Flag`}
                      className="w-20 h-14 object-cover rounded-lg border border-slate-200 shadow-sm shrink-0 mt-1"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div>
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <h2 className="text-3xl font-extrabold text-[#002B49] tracking-tight">
                        {activeState.state}
                      </h2>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300">
                        {activeState.stateCode}
                      </span>
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                        {activeState.region} Region
                      </span>
                      {activeState.isRegistered ? (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                          <ShieldCheck className="w-3.5 h-3.5" />
                          Registered in Vault
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          Unregistered in Vault
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      Capital: <span className="font-semibold text-slate-800">{activeState.capital}</span> • NASPO State ID: <span className="font-mono text-slate-800">{activeState.stateID}</span>
                    </p>
                  </div>
                </div>

                {/* State Readiness Assessment Quick Card for Active Entity */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-700 min-w-[300px]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Entity Readiness Score
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded">
                      {vendorProfile?.label || 'Active Entity'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-2xl font-black text-[#002B49]">
                        {activeReadiness?.overallScore || 0}%
                      </div>
                      <div className={`text-[11px] font-bold ${
                        (activeReadiness?.overallScore || 0) >= 85 ? 'text-emerald-700' :
                        (activeReadiness?.overallScore || 0) >= 60 ? 'text-amber-700' : 'text-rose-700'
                      }`}>
                        {activeReadiness?.tier || 'Assessing...'}
                      </div>
                    </div>

                    <button
                      onClick={() => setActiveDossierTab('readiness')}
                      className="px-3 py-1.5 bg-[#002B49] hover:bg-[#003B66] text-white font-bold rounded-lg text-xs flex items-center gap-1 transition-colors cursor-pointer"
                    >
                      <Gauge className="w-3.5 h-3.5 text-amber-400" />
                      <span>View Audit</span>
                    </button>
                  </div>

                  <div className="mt-2.5 pt-2 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500">
                    <span>SOS Standing:</span>
                    <span className="font-semibold text-slate-800">{activeReadiness?.sosStatus}</span>
                  </div>
                </div>
              </div>

              {/* Quick Portal Action Buttons */}
              <div className="mt-6 pt-5 border-t border-slate-100 flex flex-wrap items-center gap-3">
                {activeState.stateprocpubsolurl && (
                  <a
                    href={activeState.stateprocpubsolurl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#002B49] hover:bg-[#003B66] text-white text-xs font-bold rounded-lg flex items-center gap-2 shadow-sm transition-colors"
                  >
                    <Globe className="w-4 h-4 text-amber-400" />
                    <span>State Solicitations & Bids</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-300" />
                  </a>
                )}

                {activeState.stateprocpubcontractsurl && (
                  <a
                    href={activeState.stateprocpubcontractsurl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold rounded-lg flex items-center gap-2 shadow-sm transition-colors"
                  >
                    <Award className="w-4 h-4 text-amber-400" />
                    <span>Public Contracts Database</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-300" />
                  </a>
                )}

                {activeState.vendorUrl && (
                  <a
                    href={activeState.vendorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#004F71] hover:bg-[#00608A] text-white text-xs font-bold rounded-lg flex items-center gap-2 shadow-sm transition-colors"
                  >
                    <Landmark className="w-4 h-4 text-amber-300" />
                    <span>Vendor Registration Portal</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-300" />
                  </a>
                )}

                {activeState.loginUrl && (
                  <a
                    href={activeState.loginUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-lg flex items-center gap-2 border border-slate-300 transition-colors"
                  >
                    <Lock className="w-4 h-4 text-slate-600" />
                    <span>Supplier Login</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
                  </a>
                )}
              </div>
            </div>

            {/* 2. TABBED DOSSIER NAVIGATION */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              
              {/* Tab Header Bar */}
              <div className="flex border-b border-slate-200 bg-slate-50 overflow-x-auto">
                <button
                  onClick={() => setActiveDossierTab('overview')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'overview'
                      ? 'border-[#002B49] text-[#002B49] bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  <span>State Profile</span>
                </button>

                {/* NEW INTEGRATED READINESS ASSESSMENT TAB */}
                <button
                  onClick={() => setActiveDossierTab('readiness')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'readiness'
                      ? 'border-indigo-600 text-indigo-700 bg-white'
                      : 'border-transparent text-slate-600 hover:text-indigo-700 hover:bg-slate-100'
                  }`}
                >
                  <Gauge className="w-4 h-4 text-indigo-600" />
                  <span>Readiness Assessment</span>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-bold ${
                    (activeReadiness?.overallScore || 0) >= 85 ? 'bg-emerald-100 text-emerald-800' :
                    (activeReadiness?.overallScore || 0) >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                  }`}>
                    {activeReadiness?.overallScore || 0}%
                  </span>
                </button>

                <button
                  onClick={() => setActiveDossierTab('team')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'team'
                      ? 'border-[#002B49] text-[#002B49] bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Leadership & Staff</span>
                  <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-slate-200 text-slate-700">
                    {activeState.team?.length || 0}
                  </span>
                </button>

                <button
                  onClick={() => setActiveDossierTab('rosp')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'rosp'
                      ? 'border-[#002B49] text-[#002B49] bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <Scale className="w-4 h-4" />
                  <span>Rules of State Procurement (ROSP)</span>
                  <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-slate-200 text-slate-700">
                    {activeState.rosp?.length || 0}
                  </span>
                </button>

                <button
                  onClick={() => setActiveDossierTab('doingBusiness')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'doingBusiness'
                      ? 'border-[#002B49] text-[#002B49] bg-white'
                      : 'border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  <HelpCircle className="w-4 h-4" />
                  <span>Doing Business</span>
                  <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-slate-200 text-slate-700">
                    {activeState.doingBusiness?.length || 0}
                  </span>
                </button>

                {/* Integrated Enhancement: Vendor Credentials Vault */}
                <button
                  onClick={() => setActiveDossierTab('credentials')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'credentials'
                      ? 'border-emerald-600 text-emerald-800 bg-white'
                      : 'border-transparent text-slate-600 hover:text-emerald-700 hover:bg-slate-100'
                  }`}
                >
                  <Lock className="w-4 h-4 text-emerald-600" />
                  <span>Vendor Credentials Vault</span>
                  {activeState.isRegistered && (
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  )}
                </button>

                {/* Integrated Enhancement: 50-State Registrar Automation */}
                <button
                  onClick={() => setActiveDossierTab('automation')}
                  className={`px-5 py-3.5 text-xs font-bold flex items-center gap-2 whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                    activeDossierTab === 'automation'
                      ? 'border-purple-600 text-purple-800 bg-white'
                      : 'border-transparent text-slate-600 hover:text-purple-700 hover:bg-slate-100'
                  }`}
                >
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span>Registrar Automation</span>
                </button>
              </div>

              {/* TAB 1: OVERVIEW / STATE PROFILE */}
              {activeDossierTab === 'overview' && (
                <div className="p-6 space-y-6">
                  
                  {/* Summary & Mission */}
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <h3 className="text-sm font-bold text-[#002B49] uppercase tracking-wider mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-amber-500" />
                      State Purchasing Authority & Structure
                    </h3>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {activeState.summary || `Comprehensive regulatory and operational procurement guidelines for ${activeState.state}.`}
                    </p>
                  </div>

                  {/* Statutory Thresholds & Preferences */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <h4 className="text-xs font-bold text-[#002B49] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Scale className="w-4 h-4 text-[#004F71]" />
                        Statutory Bidding Thresholds
                      </h4>
                      <div className="space-y-3 text-xs">
                        <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <span className="text-slate-600">Informal Quote Threshold:</span>
                          <span className="font-bold text-slate-900">{activeState.thresholds.informalThreshold}</span>
                        </div>
                        <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <span className="text-slate-600">Formal Sealed Bid Limit:</span>
                          <span className="font-bold text-slate-900">{activeState.thresholds.formalThreshold}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 pt-1">
                          * Solicitations exceeding formal limits mandate publicly advertised RFP/IFB notices under state procurement code.
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-5 border border-slate-200">
                      <h4 className="text-xs font-bold text-[#002B49] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4 text-amber-600" />
                        Targeted Business Programs & Preferences
                      </h4>
                      <div className="space-y-2 text-xs">
                        {activeState.preferencePrograms.map((pref, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-slate-700 font-medium">
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span>{pref}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Direct Official Links Table */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 font-bold text-xs text-[#002B49] uppercase tracking-wider">
                      Official Government Portals & Repositories
                    </div>
                    <div className="divide-y divide-slate-100 text-xs">
                      {activeState.stateprocpubsolurl && (
                        <div className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-50">
                          <span className="font-medium text-slate-700">Public Solicitations / Bids Portal:</span>
                          <a href={activeState.stateprocpubsolurl} target="_blank" rel="noopener noreferrer" className="font-mono text-[#004F71] hover:underline flex items-center gap-1">
                            {activeState.stateprocpubsolurl}
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </div>
                      )}
                      {activeState.stateprocpubcontractsurl && (
                        <div className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-50">
                          <span className="font-medium text-slate-700">Awarded State Contracts:</span>
                          <a href={activeState.stateprocpubcontractsurl} target="_blank" rel="noopener noreferrer" className="font-mono text-[#004F71] hover:underline flex items-center gap-1">
                            {activeState.stateprocpubcontractsurl}
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </div>
                      )}
                      {activeState.vendorUrl && (
                        <div className="p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-slate-50">
                          <span className="font-medium text-slate-700">Vendor Registration Portal:</span>
                          <a href={activeState.vendorUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-[#004F71] hover:underline flex items-center gap-1">
                            {activeState.vendorUrl}
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB: READINESS ASSESSMENT */}
              {activeDossierTab === 'readiness' && activeReadiness && (
                <div className="p-6 space-y-6">
                  
                  {/* Entity Context Header Banner */}
                  <div className="bg-gradient-to-r from-[#002B49] to-[#004F71] text-white p-6 rounded-2xl shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-400 text-slate-900 font-black text-[10px] rounded uppercase tracking-wider">
                          Active Entity Audit
                        </span>
                        <span className="text-xs text-amber-200 font-mono">
                          Target Jurisdiction: {activeState.state} ({activeState.stateCode})
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-amber-300" />
                        {vendorProfile?.companyName || 'Active Business Entity'}
                      </h3>
                      <p className="text-xs text-slate-200 max-w-2xl leading-relaxed">
                        Statutory compliance score assessing Secretary of State standing, Tax Identifiers, e-Procurement Portal registration, NAICS/NIGP commodity mapping, and preference programs for {activeState.state}.
                      </p>
                    </div>

                    {/* Circular / Score Box */}
                    <div className="bg-white/10 border border-white/20 p-5 rounded-2xl flex items-center gap-5 shrink-0">
                      <div className="text-center">
                        <div className="text-4xl font-black text-amber-300 font-mono">
                          {activeReadiness.overallScore}%
                        </div>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-300 mt-0.5">
                          Readiness Score
                        </div>
                      </div>
                      <div className="h-10 w-px bg-white/20" />
                      <div className="space-y-1">
                        <div className="text-xs font-bold text-white">
                          {activeReadiness.passedCount} of {activeReadiness.totalCount} Checks Passed
                        </div>
                        <div className="text-[11px] text-emerald-300 font-medium flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {activeReadiness.tier}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 6-Point Compliance Audit List */}
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-[#002B49] uppercase tracking-wider flex items-center gap-2">
                      <FileCheck className="w-4 h-4 text-[#004F71]" />
                      Statutory Compliance & Eligibility Criteria Breakdown
                    </h4>

                    <div className="space-y-3">
                      {activeReadiness.items.map((item) => (
                        <div
                          key={item.id}
                          className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-slate-300 transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                                item.status === 'passed' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' :
                                item.status === 'warning' ? 'bg-amber-50 text-amber-600 border border-amber-200' :
                                'bg-rose-50 text-rose-600 border border-rose-200'
                              }`}>
                                {item.status === 'passed' ? <Check className="w-4 h-4" /> :
                                 item.status === 'warning' ? <AlertTriangle className="w-4 h-4" /> :
                                 <AlertCircle className="w-4 h-4" />}
                              </div>

                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h5 className="font-bold text-slate-900 text-sm">{item.title}</h5>
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    item.status === 'passed' ? 'bg-emerald-100 text-emerald-800' :
                                    item.status === 'warning' ? 'bg-amber-100 text-amber-800' :
                                    'bg-rose-100 text-rose-800'
                                  }`}>
                                    {item.badge}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.description}</p>
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <div className="text-xs font-mono font-bold text-slate-800">
                                {item.score} / {item.maxScore} pts
                              </div>
                            </div>
                          </div>

                          {/* Details / Remediation */}
                          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <span className="text-slate-500 text-[11px]">{item.details}</span>
                            {item.actionUrl && (
                              <a
                                href={item.actionUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1 bg-slate-100 hover:bg-[#002B49] hover:text-white text-[#002B49] font-bold rounded-lg text-[11px] flex items-center gap-1 transition-colors self-start sm:self-auto shrink-0"
                              >
                                <span>{item.actionLabel || 'Action'}</span>
                                <ArrowUpRight className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Info className="w-4 h-4 text-[#004F71]" />
                      <span>Switch business entities anytime using the profile switcher in the header to recalculate standing.</span>
                    </div>
                    <button
                      onClick={() => setActiveDossierTab('credentials')}
                      className="px-4 py-2 bg-[#002B49] hover:bg-[#003B66] text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <Lock className="w-3.5 h-3.5 text-amber-400" />
                      <span>Manage Vault Credentials for {activeState.state}</span>
                    </button>
                  </div>
                </div>
              )}

              {/* TAB 2: TEAM & LEADERSHIP */}
              {activeDossierTab === 'team' && (
                <div className="p-6 space-y-6">
                  
                  {/* Search Staff Bar */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h3 className="text-base font-bold text-[#002B49]">
                        Procurement Leadership & Regional Officers (RSPOs)
                      </h3>
                      <p className="text-xs text-slate-500">
                        Official contact information for state purchasing directors and contract managers.
                      </p>
                    </div>
                    <div className="relative w-full sm:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Filter staff by name or title..."
                        value={staffSearch}
                        onChange={(e) => setStaffSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#002B49]"
                      />
                    </div>
                  </div>

                  {/* Featured CPO / State Purchasing Director Card */}
                  {activeState.cpo && (
                    <div className="bg-gradient-to-r from-[#002B49] to-[#004F71] text-white p-6 rounded-2xl shadow-sm border border-[#001D33]">
                      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                        {activeState.cpo.imageUrl ? (
                          <img
                            src={activeState.cpo.imageUrl}
                            alt={activeState.cpo.name}
                            className="w-24 h-24 rounded-full object-cover border-2 border-amber-400 shadow-md shrink-0"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-amber-400 flex items-center justify-center text-amber-300 shrink-0">
                            <Users className="w-10 h-10" />
                          </div>
                        )}
                        <div className="flex-1 text-center sm:text-left space-y-1.5">
                          <div className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-400 text-slate-900 uppercase tracking-wider mb-1">
                            State Purchasing Director / CPO
                          </div>
                          <h4 className="text-xl font-bold text-white">{activeState.cpo.name}</h4>
                          <p className="text-xs text-slate-200">{activeState.cpo.title}</p>
                          <p className="text-xs text-slate-300 font-medium">{activeState.agencyName}</p>

                          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 pt-3">
                            {activeState.cpo.email && (
                              <a
                                href={`mailto:${activeState.cpo.email}`}
                                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-xs text-white flex items-center gap-1.5 transition-colors"
                              >
                                <Mail className="w-3.5 h-3.5 text-amber-300" />
                                <span>{activeState.cpo.email}</span>
                              </a>
                            )}
                            {activeState.cpo.phone && (
                              <a
                                href={`tel:${activeState.cpo.phone}`}
                                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-md text-xs text-white flex items-center gap-1.5 transition-colors"
                              >
                                <Phone className="w-3.5 h-3.5 text-amber-300" />
                                <span>{activeState.cpo.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Staff Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTeam.map((member, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between space-y-3"
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <h5 className="font-bold text-slate-900 text-sm">{member.name}</h5>
                            {member.isDirector && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-900 uppercase">
                                Director
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 font-medium mt-0.5">{member.title}</p>
                        </div>

                        <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs">
                          {member.email && (
                            <div className="flex items-center justify-between gap-1 text-slate-600">
                              <a href={`mailto:${member.email}`} className="text-[#004F71] hover:underline flex items-center gap-1.5 truncate">
                                <Mail className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">{member.email}</span>
                              </a>
                              <button
                                onClick={() => handleCopy(member.email, `email-${idx}`)}
                                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                                title="Copy email"
                              >
                                {copiedText === `email-${idx}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                          {member.phone && (
                            <div className="flex items-center justify-between gap-1 text-slate-600">
                              <a href={`tel:${member.phone}`} className="text-slate-700 hover:underline flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>{member.phone}</span>
                              </a>
                              <button
                                onClick={() => handleCopy(member.phone, `phone-${idx}`)}
                                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                                title="Copy phone"
                              >
                                {copiedText === `phone-${idx}` ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3: RULES OF STATE PROCUREMENT (ROSP) */}
              {activeDossierTab === 'rosp' && (
                <div className="p-6 space-y-6">
                  
                  {/* Category Pills & Search */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {rospCategories.map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setRospCategoryFilter(cat)}
                          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors cursor-pointer ${
                            rospCategoryFilter === cat
                              ? 'bg-[#002B49] text-white font-bold'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>

                    <div className="relative w-full md:w-64">
                      <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search legal citations..."
                        value={rospSearch}
                        onChange={(e) => setRospSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#002B49]"
                      />
                    </div>
                  </div>

                  {/* ROSP Rules Accordion / Cards */}
                  <div className="space-y-4">
                    {filteredRospRules.length === 0 ? (
                      <div className="p-8 text-center bg-slate-50 rounded-xl border border-slate-200 text-slate-500 text-xs">
                        No statutory rules matching the selected category or filter.
                      </div>
                    ) : (
                      filteredRospRules.map((rule, idx) => {
                        const statusBadge = rule.status || rule.definition || rule.statusOrDefinition;
                        const ruleText = rule.citationLanguage || rule.statutoryLanguage || rule.description || '';
                        
                        return (
                          <div
                            key={idx}
                            className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm space-y-3 hover:border-slate-300 transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#004F71] text-white uppercase">
                                  {rule.category}
                                </span>
                                {rule.citation && (
                                  <span className="font-mono font-bold text-xs text-[#002B49]">
                                    {rule.citation}
                                  </span>
                                )}
                              </div>
                              {statusBadge && (
                                <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                                  {statusBadge}
                                </span>
                              )}
                            </div>

                            {ruleText && (
                              <div 
                                className="bg-slate-50 p-3.5 rounded-lg border border-slate-100 text-xs text-slate-700 leading-relaxed font-sans prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: ruleText }}
                              />
                            )}

                            {rule.links && rule.links.length > 0 && (
                              <div className="pt-2 flex flex-wrap items-center gap-3 text-xs">
                                {rule.links.map((linkItem, lIdx) => (
                                  <a
                                    key={lIdx}
                                    href={linkItem.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#004F71] hover:text-[#002B49] font-medium flex items-center gap-1 hover:underline"
                                  >
                                    <span>{linkItem.label || linkItem.title || 'Official Statutory Link'}</span>
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {/* TAB 4: DOING BUSINESS */}
              {activeDossierTab === 'doingBusiness' && (
                <div className="p-6 space-y-4">
                  <div className="mb-2">
                    <h3 className="text-base font-bold text-[#002B49]">
                      How to Do Business With {activeState.state}
                    </h3>
                    <p className="text-xs text-slate-500">
                      Official NASPO state procurement Q&A covering commodity classification, registration fees, preferences, and exempt entities.
                    </p>
                  </div>

                  <div className="divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden bg-white">
                    {activeState.doingBusiness.map((item, idx) => (
                      <div key={idx} className="p-5 hover:bg-slate-50/60 transition-colors">
                        <h4 className="font-bold text-sm text-[#002B49] flex items-center gap-2 mb-2">
                          <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-900 text-xs flex items-center justify-center font-bold shrink-0">
                            {idx + 1}
                          </span>
                          <span>{item.heading}</span>
                        </h4>
                        <div
                          className="text-xs text-slate-700 leading-relaxed pl-7 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: item.content }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 5: VENDOR CREDENTIALS VAULT (Integrated Enhancement - Synced with Active Entity) */}
              {activeDossierTab === 'credentials' && (
                <div className="p-6 space-y-6">
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-emerald-50 border border-emerald-200 p-4 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                        <Lock className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-emerald-950 text-sm">
                            {activeState.state} State Vendor Portal Vault
                          </h4>
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-200 text-emerald-900 rounded font-bold">
                            {vendorProfile?.label || 'Active Entity'}
                          </span>
                        </div>
                        <p className="text-xs text-emerald-800 mt-0.5">
                          Persisting credentials for <strong className="font-semibold">{vendorProfile?.companyName || 'Authenticated Vendor'}</strong> in Cloud Firestore.
                        </p>
                      </div>
                    </div>

                    {!isEditingCredentials && (
                      <button
                        onClick={() => setIsEditingCredentials(true)}
                        className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors self-start sm:self-auto cursor-pointer"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        <span>Edit Credentials</span>
                      </button>
                    )}
                  </div>

                  {isEditingCredentials ? (
                    /* EDIT FORM */
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                            State Vendor / Supplier ID
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. TX-984210 or 00004921"
                            value={formVendorId}
                            onChange={(e) => setFormVendorId(e.target.value)}
                            className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                            Portal Username / Email
                          </label>
                          <input
                            type="text"
                            placeholder="portal_user@company.com"
                            value={formUsername}
                            onChange={(e) => setFormUsername(e.target.value)}
                            className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                            Portal Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              placeholder="••••••••••••"
                              value={formPassword}
                              onChange={(e) => setFormPassword(e.target.value)}
                              className="w-full px-3 py-2 pr-10 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 cursor-pointer"
                            >
                              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        <div className="sm:col-span-2 flex items-center gap-2 pt-2">
                          <input
                            type="checkbox"
                            id="regCheck"
                            checked={formIsRegistered}
                            onChange={(e) => setFormIsRegistered(e.target.checked)}
                            className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                          />
                          <label htmlFor="regCheck" className="text-xs font-medium text-slate-700 cursor-pointer">
                            Mark as actively registered in {activeState.state}'s central supplier registry for <strong>{vendorProfile?.companyName}</strong>
                          </label>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => setIsEditingCredentials(false)}
                          className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveCredentials}
                          disabled={savingCredentials}
                          className="px-5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                        >
                          {savingCredentials ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          <span>Save & Sync to Entity Profile</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* VIEW SAVED CREDENTIALS */
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">State Vendor ID</div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-sm text-slate-900">
                            {activeState.vendorId || <span className="text-slate-400 font-normal italic">Not recorded</span>}
                          </span>
                          {activeState.vendorId && (
                            <button
                              onClick={() => handleCopy(activeState.vendorId || '', 'vid')}
                              className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                              title="Copy Vendor ID"
                            >
                              {copiedText === 'vid' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Username / Login</div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-sm text-slate-900 truncate">
                            {activeState.username || <span className="text-slate-400 font-normal italic">Not recorded</span>}
                          </span>
                          {activeState.username && (
                            <button
                              onClick={() => handleCopy(activeState.username || '', 'user')}
                              className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                              title="Copy Username"
                            >
                              {copiedText === 'user' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 sm:col-span-2">
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Portal Password</div>
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-sm text-slate-900">
                            {activeState.password ? (
                              showPassword ? activeState.password : '••••••••••••'
                            ) : (
                              <span className="text-slate-400 font-normal italic">Not recorded</span>
                            )}
                          </span>
                          {activeState.password && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setShowPassword(!showPassword)}
                                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                                title="Toggle visibility"
                              >
                                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => handleCopy(activeState.password || '', 'pwd')}
                                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
                                title="Copy Password"
                              >
                                {copiedText === 'pwd' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 6: 50-STATE REGISTRAR AUTOMATION (Multi-Entity Integrated) */}
              {activeDossierTab === 'automation' && (
                <div className="p-6 space-y-6">
                  
                  <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-purple-950 text-sm">
                        50-State Multi-Registry Automated Conduit
                      </h4>
                      <p className="text-xs text-purple-800">
                        Synchronize <strong className="font-semibold">{vendorProfile?.companyName || 'your active business entity'}</strong> across all 50 state procurement portals with verified commodity codes.
                      </p>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-xl border border-slate-200">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setFilterMode('hot');
                          setSelectedBulkStates(['TX', 'CA', 'NY', 'FL', 'IL', 'PA', 'OH']);
                        }}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer ${
                          filterMode === 'hot' ? 'bg-[#002B49] text-white border-[#002B49]' : 'border-slate-200 text-slate-600'
                        }`}
                      >
                        Top 7 Tier-1 States
                      </button>
                      <button
                        onClick={() => {
                          setFilterMode('all');
                          setSelectedBulkStates(ALL_NASPO_STATES.map(s => s.stateCode));
                        }}
                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border cursor-pointer ${
                          filterMode === 'all' ? 'bg-[#002B49] text-white border-[#002B49]' : 'border-slate-200 text-slate-600'
                        }`}
                      >
                        All 50 States + DC ({ALL_NASPO_STATES.length})
                      </button>
                    </div>

                    <button
                      onClick={runBulkRegistration}
                      disabled={runningPipeline || selectedBulkStates.length === 0}
                      className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-xs rounded-lg shadow-md flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                      {runningPipeline ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Processing Pipeline for {vendorProfile?.companyName?.slice(0, 15)}...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 text-amber-300" />
                          <span>Run Multi-State Sync for {vendorProfile?.companyName?.slice(0, 15) || 'Entity'} ({selectedBulkStates.length})</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Terminal Execution Log */}
                  <div className="bg-slate-900 text-emerald-400 p-4 rounded-xl font-mono text-xs shadow-inner h-64 overflow-y-auto" ref={terminalEndRef}>
                    <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800 text-slate-400 text-[11px]">
                      <span className="flex items-center gap-1.5">
                        <Terminal className="w-3.5 h-3.5 text-slate-400" />
                        <span>REGISTRAR AUTOMATION ENGINE LIVE LOG</span>
                      </span>
                      <span>STATUS: {runningPipeline ? 'RUNNING' : 'IDLE'}</span>
                    </div>
                    {pipelineLogs.length === 0 ? (
                      <div className="text-slate-600 italic">No execution logs yet. Click 'Run Multi-State Sync' to initiate registration pipeline.</div>
                    ) : (
                      pipelineLogs.map((log, idx) => (
                        <div key={idx} className="py-0.5 leading-relaxed">
                          {log}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
